/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FlexContainer, Heading, Sidebar, Text, theme } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { CloudResource } from "~/components/EstateRecordsList";
import { isCloudResource, isNotNil, Maybe } from "~/tools";

type Record = {
  __typename?: "CloudResource";
  id: string;
  name: string;
  providerId: string;
  provider: Provider;
  providerType?: Maybe<string>;
};

export type LinkedResource = {
  __typename?: "Link";
  sourceRecord: Record | { __typename?: "CostResource" };
  targetRecord: Record | { __typename?: "CostResource" };
};

type ReallocatedCostsSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  linkedResources?: LinkedResource[];
  provider?: Provider;
};

export function ReallocatedCostsSidebar({
  isOpen,
  onClose,
  linkedResources,
  provider,
}: ReallocatedCostsSidebarProps) {
  const records = React.useMemo(
    () => getCloudResources(provider, linkedResources),
    [provider, linkedResources]
  );

  const navigate = useNavigate();

  const onResourceClick = (id: string) => {
    navigate(id);
    onClose();
  };

  return (
    <Sidebar
      isOpen={isOpen}
      title="View Reallocated Resources"
      onClick={onClose}
    >
      {records.map((item, index) => (
        <SidebarItemContainer key={`reallocations.item.${item.id}`}>
          <FlexContainer
            direction="column"
            alignItems="flex-start"
            onClick={() => onResourceClick(item.id)}
          >
            <Heading level={4}>{item.name}</Heading>
            <Text
              size="sm"
              tag="div"
              color={theme.color.text.text02}
              data-testid={"reallocation." + index}
            >
              {item.id}
            </Text>
          </FlexContainer>
        </SidebarItemContainer>
      ))}
    </Sidebar>
  );
}

const SidebarItemContainer = styled.div`
  padding: ${theme.spacing.spacing02};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid ${theme.color.border.border01};
  user-select: none;

  &:hover {
    background-color: ${theme.color.background.ui03};
  }
`;

function getCloudResources(
  provider?: Provider,
  items: LinkedResource[] = []
): CloudResource[] {
  const links = items.filter(({ sourceRecord, targetRecord }) =>
    isCloudResource(
      provider === Provider.Kubernetes ? sourceRecord : targetRecord
    )
  );

  return links
    .map(
      ({ sourceRecord, targetRecord }) =>
        (provider === Provider.Kubernetes
          ? sourceRecord
          : targetRecord) as CloudResource
    )
    .filter(isNotNil);
}
